<!-- src/app/timesheets/components/admin-timesheet/admin-timesheet.component.html -->
<div
  class="admin-timesheet-wrapper"
  [class.payroll-locked]="isPayrollSynced"
  [class.modal-open]="isAddPunchOpen || approvalModalOpen"
>
  <div
    class="toast show"
    *ngIf="toastMessage"
    [class.success]="toastKind === 'success'"
    [class.error]="toastKind === 'error'"
    [class.warning]="toastKind === 'info'"
    (click)="clearToast()"
    role="status"
    aria-live="polite"
  >
    <img
      class="icon"
      [src]="
        toastKind === 'success'
          ? '/success.png'
          : toastKind === 'error'
            ? '/fail.png'
            : '/warning.png'
      "
      alt=""
      aria-hidden="true"
    />

    <div class="toast-title">
      {{ toastKind === 'success' ? 'Success' : toastKind === 'error' ? 'Action needed' : 'Notice' }}
    </div>

    <div class="toast-body">{{ toastMessage }}</div>
  </div>

  <div *ngIf="isLoading || isSaving" class="loading-overlay" aria-label="Loading">
    <div class="spinner"></div>
  </div>

  <div
    *ngIf="isPunchActionBusy || isApprovalBusy || isExportingTimesheet"
    class="loading-overlay punch-loading"
    aria-label="Working"
  >
    <div class="spinner"></div>
  </div>

  <div *ngIf="errorMessage" class="error-message">
    {{ errorMessage }}
  </div>

  <ng-container *ngIf="!isLoading && employee">
    <div class="overview-row">
      <button class="back-btn" type="button" (click)="goBackToOverview()">
        ← Back to Overview
      </button>
    </div>

    <div class="week-nav">
      <div class="week-nav-left">
        <button class="nav-btn" type="button" (click)="prevWeek()">← Previous Week</button>
      </div>

      <div class="week-nav-center">
        <div class="week-nav-label">Timesheet Week</div>
        <div class="week-nav-value">{{ currentWeekLabel }}</div>
      </div>

      <div class="nav-actions">
        <button class="nav-btn" type="button" (click)="nextWeek()">Next Week →</button>
      </div>
    </div>

    <div class="header">
      <div class="title">
        <h2>
          Admin Timesheet — {{ employee.lastName }}, {{ employee.firstName }}
          <span class="meta">(ID: {{ employee.id }} • Emp#: {{ employee.employeeNumber || '-' }})</span>
        </h2>

        <button
          type="button"
          class="export-timesheet-btn"
          (click)="exportTimesheetToExcel()"
          [disabled]="isExportingTimesheet || isLoading || !employee"
          title="Export this employee timesheet to Excel"
        >
          {{ isExportingTimesheet ? 'Exporting...' : 'Export Excel' }}
        </button>
      </div>
    </div>

    <div class="payroll-lock-banner" *ngIf="isPayrollSynced" role="status" aria-live="polite">
      <div class="lock-copy">
        <div class="lock-title">Payroll Sync Completed</div>
        <div class="lock-message">{{ payrollLockedMessage }}</div>
      </div>
    </div>

    <div class="legend legend-compact" aria-label="Legend">
      <div class="legend-item">
        <span class="swatch approved"></span>
        <span>Supervisor Approved</span>
      </div>

      <div class="legend-item">
        <span class="swatch match"></span>
        <span>Clocked = Booked</span>
      </div>

      <div class="legend-item">
        <span class="swatch under"></span>
        <span>Clocked &gt; Booked</span>
      </div>

      <div class="legend-item">
        <span class="swatch over"></span>
        <span>Clocked &lt; Booked</span>
      </div>

      <div class="legend-item">
        <span class="swatch missing-punch"></span>
        <span>Missing Punch</span>
      </div>

      <div class="legend-item">
        <span class="swatch paycode-holiday"></span>
        <span>Holiday</span>
      </div>

      <div class="legend-item">
        <span class="swatch requested-offtime"></span>
        <span>Requested Offtime</span>
      </div>
    </div>

    <div class="save-row">
      <div class="actions">
        <ng-container *ngIf="isDirty && !isPayrollSynced">
          <button class="btn secondary" type="button" (click)="cancelChanges()">Cancel</button>
          <button class="btn primary" type="button" (click)="saveWeek()">Save</button>
        </ng-container>
      </div>
    </div>

    <div
      class="table-wrapper"
      [class.locked-section]="isPayrollSynced"
      [attr.aria-readonly]="isPayrollSynced"
    >
      <table class="admin-table admin-table-compact">
        <thead>
          <tr>
            <th class="won-col">
              <div class="won-head">
                <span>WON</span>

                <button
                  type="button"
                  class="add-row-under"
                  (click)="addWorkOrderRow()"
                  [disabled]="isPayrollSynced"
                  [attr.tabindex]="isPayrollSynced ? -1 : null"
                  [title]="
                    isPayrollSynced
                      ? 'Timesheet is locked because payroll sync has already been run'
                      : 'Add WON row'
                  "
                  aria-label="Add WON row"
                >
                  +
                </button>
              </div>
            </th>

            <th class="paycode-col">Pay</th>

            <th *ngFor="let dayIndex of [0, 1, 2, 3, 4, 5, 6]" class="day-head">
              <div class="day-head-wrap">
                <div class="day-label">{{ getDayLabel(dayIndex) }}</div>
                <div class="date" *ngIf="weekDates[dayIndex]">{{ weekDates[dayIndex] }}</div>

                <div class="approval-actions">
                  <button
                    type="button"
                    class="approval-icon approve"
                    title="Approve this day"
                    [disabled]="isPayrollSynced || !hasApprovalItemsForDay(dayIndex) || isApprovalBusy"
                    [attr.tabindex]="isPayrollSynced ? -1 : null"
                    (click)="openApproveDay(dayIndex)"
                  >
                    ✓
                  </button>

                  <button
                    type="button"
                    class="approval-icon change"
                    title="Request changes for this day"
                    [disabled]="isPayrollSynced || !hasApprovalItemsForDay(dayIndex) || isApprovalBusy"
                    [attr.tabindex]="isPayrollSynced ? -1 : null"
                    (click)="openChangeDay(dayIndex)"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </th>

            <th class="total-col total-head">
              <div class="day-head-wrap">
                <div class="day-label">Total</div>
                <div class="date date-placeholder">Full Week</div>

                <div class="approval-actions">
                  <button
                    type="button"
                    class="approval-icon approve"
                    title="Approve full week"
                    [disabled]="isPayrollSynced || !hasApprovalItemsForWeek() || isApprovalBusy"
                    [attr.tabindex]="isPayrollSynced ? -1 : null"
                    (click)="openApproveWeek()"
                  >
                    ✓
                  </button>

                  <button
                    type="button"
                    class="approval-icon change"
                    title="Request changes for full week"
                    [disabled]="isPayrollSynced || !hasApprovalItemsForWeek() || isApprovalBusy"
                    [attr.tabindex]="isPayrollSynced ? -1 : null"
                    (click)="openChangeWeek()"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            *ngFor="let row of rows"
            [class.is-new]="row.isNew"
            [class.pending-delete]="row.pendingDelete"
            [class.locked-row]="isPayrollSynced"
          >
            <td class="won-col">
              <ng-container *ngIf="!row.isNew; else newWonPicker">
                <div class="won-readonly">
                  <div class="won-text" [title]="row.workOrderNumberLabel">
                    {{ row.workOrderNumberLabel }}
                  </div>

                  <button
                    type="button"
                    class="row-delete"
                    [class.pending]="row.pendingDelete"
                    [title]="
                      row.pendingDelete
                        ? 'Marked for delete (will apply on Save)'
                        : 'Remove this WON from the week (requires Save)'
                    "
                    aria-label="Remove WON row"
                    [disabled]="isPayrollSynced"
                    [attr.tabindex]="isPayrollSynced ? -1 : null"
                    (click)="deleteWonRow(row)"
                  >
                    ✕
                  </button>
                </div>
              </ng-container>

              <ng-template #newWonPicker>
                <div class="editable-select" [class.disabled]="isPayrollSynced || !userWonOptions.length">
                  <select
                    [(ngModel)]="row.workOrderNumberId"
                    (ngModelChange)="onNewRowWonChange(row)"
                    [disabled]="isPayrollSynced"
                    [attr.tabindex]="isPayrollSynced ? -1 : null"
                  >
                    <option [ngValue]="null">Select WON…</option>
                    <option *ngFor="let option of userWonOptions" [ngValue]="option.workOrderNumberId">
                      {{ option.won }}
                    </option>
                  </select>
                </div>
              </ng-template>
            </td>

            <td class="paycode-col">
              <ng-container *ngIf="!row.isNew; else newPayCodePicker">
                <span class="paycode-pill">{{ row.payCodeName || '-' }}</span>
              </ng-container>

              <ng-template #newPayCodePicker>
                <div
                  class="editable-select"
                  [class.disabled]="isPayrollSynced || !filterService.filterValues?.paycodes?.length"
                >
                  <select
                    [(ngModel)]="row.payCodeId"
                    (ngModelChange)="onNewRowPayCodeChange(row)"
                    [disabled]="isPayrollSynced"
                    [attr.tabindex]="isPayrollSynced ? -1 : null"
                  >
                    <option [ngValue]="null">Paycode</option>
                    <option
                      *ngFor="let payCode of filterService.filterValues?.paycodes"
                      [ngValue]="payCode.id"
                    >
                      {{ payCode.name }}
                    </option>
                  </select>
                </div>
              </ng-template>
            </td>

            <td *ngFor="let dayIndex of [0, 1, 2, 3, 4, 5, 6]" class="day-cell">
              <div
                class="editable-input"
                [class.disabled]="isBookedHoursInputDisabled(row)"
              >
                <input
                  class="booked-hours-input no-spinner"
                  type="number"
                  min="0"
                  step="0.25"
                  inputmode="decimal"
                  [disabled]="isBookedHoursInputDisabled(row)"
                  [readonly]="isPayrollSynced"
                  [attr.tabindex]="isPayrollSynced ? -1 : null"
                  [ngModel]="row.dailyBooked[dayIndex]"
                  (ngModelChange)="onBookedHoursInput(row, dayIndex, $event)"
                  (focus)="onHourFocus($event)"
                  (blur)="onHourBlur(row, dayIndex, $event)"
                />
              </div>
            </td>

            <td class="total-col">
              {{ row.rowTotal | number: '1.2-2' }}
            </td>
          </tr>
        </tbody>

        <tfoot>
          <tr class="summary">
            <td class="label" colspan="2">Booked</td>

            <td
              *ngFor="let dayIndex of [0, 1, 2, 3, 4, 5, 6]"
              class="summary-cell"
              [ngClass]="getDailySummaryClass(dayIndex)"
            >
              {{ dailyBookedTotals[dayIndex] | number: '1.2-2' }}
            </td>

            <td class="total-col">{{ weeklyBookedTotal | number: '1.2-2' }}</td>
          </tr>

          <tr class="summary">
            <td class="label" colspan="2">Clocked</td>

            <td *ngFor="let dayIndex of [0, 1, 2, 3, 4, 5, 6]" class="summary-cell with-tooltip">
              <span class="tooltip-anchor">
                {{ dailyClocked[dayIndex] | number: '1.2-2' }}

                <span class="tooltip" *ngIf="formattedPunches[dayIndex]">
                  {{ formattedPunches[dayIndex] }}
                </span>
              </span>
            </td>

            <td class="total-col">{{ weeklyClockedTotal | number: '1.2-2' }}</td>
          </tr>

          <tr class="summary">
            <td class="label" colspan="2">Sched</td>

            <td *ngFor="let dayIndex of [0, 1, 2, 3, 4, 5, 6]" class="summary-cell with-tooltip">
              <ng-container *ngIf="dailyScheduled[dayIndex] as scheduled; else noSchedule">
                <span>{{ getDurationHours(scheduled).totalHours | number: '1.2-2' }}</span>

                <span class="tooltip">
                  {{ scheduled.schedule.localStartTime }} - {{ scheduled.schedule.localEndTime }}
                </span>

                <span style="font-size: x-small; display: block;">
                  ({{ getDurationHours(scheduled).breakHours | number: '1.2-2' }})
                </span>
              </ng-container>

              <ng-template #noSchedule>
                <span>{{ 0 | number: '1.2-2' }}</span>
                <span style="font-size: x-small; display: block;">
                  ({{ 0 | number: '1.2-2' }})
                </span>
              </ng-template>
            </td>

            <td class="total-col">{{ weeklyScheduledTotal | number: '1.2-2' }}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <div class="save-bypass-row" *ngIf="hasSaveBypassEmployeeApprovalOption && !isPayrollSynced">
      <label class="save-bypass-inline" [class.active]="saveBypassEmployeeApproval" [class.disabled]="isSaving">
        <input
          type="checkbox"
          [ngModel]="saveBypassEmployeeApproval"
          (ngModelChange)="onSaveBypassEmployeeApprovalChange($event)"
          [disabled]="isSaving"
        />

        <span>Bypass employee approval when saving changed booked hours</span>
      </label>
    </div>

    <div class="punches-card" [class.locked-section]="isPayrollSynced" aria-label="Punches">
      <div class="punches-head">
        <div class="punches-title">
          <div class="h">Punches</div>

          <div class="sub">
            <span *ngIf="weekDates.length">
              Selected day: {{ getDayLabel(selectedDayIndex) }} • {{ weekDates[selectedDayIndex] }}
            </span>
          </div>
        </div>

        <div class="punches-actions">
          <button
            type="button"
            class="btn small"
            (click)="openAddPunch()"
            [disabled]="isPayrollSynced || isPunchActionBusy || !employee.rfid"
            [attr.tabindex]="isPayrollSynced ? -1 : null"
            [title]="
              isPayrollSynced
                ? 'Timesheet is locked because payroll sync has already been run'
                : !employee.rfid
                  ? 'Employee has no RFID'
                  : 'Add manual punch'
            "
          >
            + Add Punch
          </button>
        </div>
      </div>

      <div class="punch-day-tabs" role="tablist" aria-label="Select day">
        <button
          *ngFor="let dayIndex of [0, 1, 2, 3, 4, 5, 6]"
          type="button"
          class="tab"
          role="tab"
          [class.active]="selectedDayIndex === dayIndex"
          [attr.aria-selected]="selectedDayIndex === dayIndex"
          (click)="onSelectPunchDay(dayIndex)"
          [disabled]="!weekDates[dayIndex]"
        >
          <div class="tab-top">{{ getDayLabel(dayIndex) }}</div>
          <div class="tab-sub">{{ weekDates[dayIndex] || '' }}</div>
        </button>
      </div>

      <div class="punch-totals">
        <div class="label">Total clocked</div>
        <div class="value">{{ selectedDayTotalClockedHours | number: '1.2-2' }} hrs</div>
      </div>

      <div class="punch-table">
        <div class="punch-row punch-row-head" aria-hidden="true">
          <div class="c time">Time</div>
          <div class="c type">Type</div>
          <div class="c source">Source</div>
          <div class="c device">Device</div>
          <div class="c updated-by">Updated By</div>
          <div class="c updated-at">Updated At</div>
          <div class="c actions"></div>
        </div>

        <ng-container *ngIf="punchEvents.length; else noPunches">
          <div
            *ngFor="let punch of punchEvents"
            class="punch-row"
            [class.busy]="isPunchActionBusy"
            [class.invalid-row]="punch.isInvalid"
            [attr.title]="getPunchRowTitle(punch)"
          >
            <div class="c time">
              <div class="primary">{{ punch.displayTime }}</div>
              <div class="timezone">{{ punch.timeZoneId }}</div>
              <div class="muted">{{ punch.displayDate }}</div>
              <div class="invalid-note" *ngIf="punch.isInvalid">Invalidated</div>
            </div>

            <div class="c type">
              <span class="pill" [class.in]="punch.type === 'In'" [class.out]="punch.type === 'Out'">
                {{ punch.type }}
              </span>
            </div>

            <div class="c source">
              <span
                class="pill"
                [class.manual]="punch.source === 'Manual'"
                [class.device]="punch.source === 'Device'"
              >
                {{ punch.source === 'Device' ? 'Clock' : 'Manual' }}
              </span>
            </div>

            <div class="c device">
              <span class="device-text" [title]="punch.deviceName || ''">
                {{ punch.deviceName || '—' }}
              </span>
            </div>

            <div class="c updated-by">
              <span class="audit-text" [class.empty]="!hasPunchAudit(punch)" [title]="getPunchUpdatedByLabel(punch)">
                {{ getPunchUpdatedByLabel(punch) }}
              </span>
            </div>

            <div class="c updated-at">
              <span class="audit-text" [class.empty]="!hasPunchAudit(punch)" [title]="getPunchUpdatedAtLabel(punch)">
                {{ getPunchUpdatedAtLabel(punch) }}
              </span>
            </div>

            <div class="c actions">
              <button
                *ngIf="!punch.isInvalid"
                type="button"
                class="link danger"
                (click)="invalidatePunch(punch.id, punch.displayDate)"
                [disabled]="isPayrollSynced || isPunchActionBusy || !canInvalidatePunch(punch)"
                [attr.tabindex]="isPayrollSynced ? -1 : null"
                [title]="
                  isPayrollSynced
                    ? 'Timesheet is locked because payroll sync has already been run'
                    : !canInvalidatePunch(punch)
                      ? 'Not allowed before last week Monday'
                      : 'Invalidate punch'
                "
                aria-label="Invalidate punch"
              >
                Invalidate
              </button>

              <span *ngIf="punch.isInvalid" class="invalid-badge">Invalid</span>
            </div>
          </div>
        </ng-container>

        <ng-template #noPunches>
          <div class="punch-empty">No punches for this day.</div>
        </ng-template>
      </div>

      <div
        class="modal-layer"
        *ngIf="isAddPunchOpen"
        (click)="closeAddPunch()"
        role="presentation"
        aria-hidden="false"
      >
        <div
          class="modal"
          role="dialog"
          aria-modal="true"
          aria-label="Add manual punch"
          (click)="$event.stopPropagation()"
        >
          <div class="modal-head">
            <div class="modal-title">Add manual punch</div>
            <button type="button" class="modal-x" (click)="closeAddPunch()" aria-label="Close">×</button>
          </div>

          <div class="modal-body">
            <div class="field-grid">
              <div class="field">
                <label>Date</label>
                <input
                  type="date"
                  [(ngModel)]="addPunchDate"
                  [disabled]="isPayrollSynced || isPunchActionBusy"
                />
              </div>

              <div class="field">
                <label>Time</label>
                <input
                  type="time"
                  [(ngModel)]="addPunchTime"
                  [disabled]="isPayrollSynced || isPunchActionBusy"
                />
              </div>
            </div>

            <div class="hint">
              RFID: <b>{{ employee.rfid || '-' }}</b>
              <span class="dot">•</span>
              Time zone: <b>{{ addPunchTimeZoneId }}</b>
            </div>
          </div>

          <div class="modal-actions">
            <button
              type="button"
              class="btn secondary"
              (click)="closeAddPunch()"
              [disabled]="isPayrollSynced || isPunchActionBusy"
            >
              Cancel
            </button>

            <button
              type="button"
              class="btn primary"
              (click)="submitAddPunch()"
              [disabled]="isPayrollSynced || isPunchActionBusy"
            >
              Add
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      class="modal-layer"
      *ngIf="approvalModalOpen"
      (click)="closeApprovalModal()"
      role="presentation"
      aria-hidden="false"
    >
      <div
        class="modal approval-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Booked hours approval"
        (click)="$event.stopPropagation()"
      >
        <div class="modal-head">
          <div class="modal-title">{{ getApprovalModalTitle() }}</div>
          <button type="button" class="modal-x" (click)="closeApprovalModal()" aria-label="Close">×</button>
        </div>

        <div class="modal-body approval-modal-body">
          <div class="approval-summary-card" [class.change-mode]="approvalAction === 'change'">
            <div class="approval-summary-icon">
              {{ approvalAction === 'approve' ? '✓' : '✕' }}
            </div>

            <div class="approval-summary-text">
              <div class="approval-summary-title">
                {{ approvalAction === 'approve' ? 'Confirm supervisor approval' : 'Request corrected hours' }}
              </div>

              <div class="approval-summary-subtitle" *ngIf="approvalAction === 'approve'">
                This will approve the booked hour records listed below and lock them from employee edits.
              </div>

              <div class="approval-summary-subtitle" *ngIf="approvalAction === 'change'">
                Update only the rows that need correction. Unchanged rows will not be submitted.
              </div>
            </div>
          </div>

          <div class="approval-list">
            <div class="approval-list-head">
              <span>Date</span>
              <span>WON</span>
              <span>Pay Code</span>
              <span>{{ approvalAction === 'approve' ? 'Hours' : 'New Hours' }}</span>
            </div>

            <div class="approval-list-row" *ngFor="let item of approvalRows">
              <div class="approval-date">
                <span class="mobile-label">Date</span>
                <strong>{{ item.date }}</strong>
              </div>

              <div class="approval-won">
                <span class="mobile-label">WON</span>
                <span class="truncate" [title]="item.won">{{ item.won }}</span>
              </div>

              <div class="approval-paycode">
                <span class="mobile-label">Pay Code</span>
                <span class="approval-pill">{{ item.payCode }}</span>
              </div>

              <div class="approval-hours">
                <span class="mobile-label">
                  {{ approvalAction === 'approve' ? 'Hours' : 'New Hours' }}
                </span>

                <ng-container *ngIf="approvalAction === 'approve'; else editableApprovalHours">
                  <span class="approval-current-hours">{{ item.currentHours | number: '1.2-2' }}</span>
                </ng-container>

                <ng-template #editableApprovalHours>
                  <input
                    class="approval-hours-input booked-hours-input no-spinner"
                    type="number"
                    min="0"
                    step="0.25"
                    inputmode="decimal"
                    [(ngModel)]="item.newHours"
                    [disabled]="isPayrollSynced || isApprovalBusy"
                    [readonly]="isPayrollSynced"
                    [attr.tabindex]="isPayrollSynced ? -1 : null"
                  />
                </ng-template>
              </div>
            </div>
          </div>

          <label class="bypass-check" *ngIf="approvalAction === 'change'">
            <input
              type="checkbox"
              [(ngModel)]="approvalBypassEmployeeApproval"
              [disabled]="isPayrollSynced || isApprovalBusy"
              [readonly]="isPayrollSynced"
              [attr.tabindex]="isPayrollSynced ? -1 : null"
            />

            <span>
              <strong>Bypass employee approval</strong>
              <small>Approve changed hours immediately instead of sending them back to the employee.</small>
            </span>
          </label>

          <div class="approval-error" *ngIf="approvalError">
            {{ approvalError }}
          </div>
        </div>

        <div class="modal-actions">
          <button type="button" class="btn secondary" (click)="closeApprovalModal()" [disabled]="isApprovalBusy">
            Cancel
          </button>

          <button
            type="button"
            class="btn primary"
            (click)="submitApprovalAction()"
            [disabled]="isPayrollSynced || isApprovalBusy"
          >
            {{ isApprovalBusy ? 'Submitting...' : approvalAction === 'approve' ? 'Approve' : 'Submit Changes' }}
          </button>
        </div>
      </div>
    </div>
  </ng-container>
</div>